import { getTripTiming } from './trip-timing.js';

const ADVICE_PATTERNS = [
  /\b(consigli|consigliami|suggerisci|suggerimenti|mi dici)\b/i,
  /\b(ristorant|mangiare|pranzo|cena|colazione|bar)\b/i,
  /\b(alternativ|in più|oltre al|extra|fuori programma|aggiungere)\b/i,
  /\b(cosa portare|scarpe|abbigliamento|equipaggiamento|zaino)\b/i,
  /\b(meteo|tempo|pioggia|temperature)\b/i,
  /\b(orari?|apert|chius|prenot|bigliett)\b/i,
  /\b(curiosit|sapere su|storia|tradizion|leggend)\b/i,
  /\b(vale la pena|conviene|worth it)\b/i,
  /\b(difficil|impegnativ|facile|salita|dislivell|sentiero)\b/i,
  /\b(cerca(?:re)? online|in rete|su internet|online)\b/i,
];

const PLAN_FACT_PATTERNS = [
  /\b(è nel|e nel|c['']?è|c'e|in programma|in itinerario|previst)\b/i,
  /\b(quale giorno|che giorno)\b/i,
  /\b(domani|oggi|dopo domani)\s+(dove|cosa|facciamo|andiamo)\b/i,
  /\b(cosa (?:c['']|e|è) oggi|programma di oggi)\b/i,
  /\b(giornata più|più pesante|più leggera|più intensa)\b/i,
  /\b(quante tappe|quante fermate)\b/i,
];

const PLACE_VERDICTS = new Set([
  'in_itinerary',
  'on_route',
  'same_area',
  'nearby',
  'far',
  'outside',
  'possible_match',
]);

/**
 * Whether to enable Gemini Google Search grounding for this message.
 * @param {{ message: string, placeAnalysis?: object | null, forceWebSearch?: boolean }} opts
 */
export function shouldUseWebSearch({ message, placeAnalysis = null, forceWebSearch = false }) {
  if (forceWebSearch) return true;

  const text = String(message ?? '').trim();
  if (!text) return false;

  if (ADVICE_PATTERNS.some((pattern) => pattern.test(text))) return true;

  const verdict = placeAnalysis?.verdict;
  const isPlaceFact =
    verdict && PLACE_VERDICTS.has(verdict) && PLAN_FACT_PATTERNS.some((pattern) => pattern.test(text));
  if (isPlaceFact) return false;

  if (verdict === 'general' && PLAN_FACT_PATTERNS.some((pattern) => pattern.test(text))) {
    return false;
  }

  return false;
}

/**
 * @param {import('../trip-context.js').getContext extends () => infer R ? R : never} ctx
 */
export function buildWelcomeMessage(ctx) {
  const trip = ctx?.trip;
  const title = trip?.title?.trim();
  const timing = trip ? getTripTiming(trip) : null;
  const label = title ? ` per ${title}` : '';

  if (!timing) {
    return `Ehi! Ho ripassato tutto il piano${label}. Chiedimi quello che vuoi sul viaggio.`;
  }

  if (timing.phase === 'upcoming') {
    const countdown =
      timing.daysToStart === 0
        ? '— partenza oggi!'
        : timing.daysToStart === 1
          ? '— partenza domani!'
          : ` — mancano ${timing.daysToStart} giorni`;
    return `Ehi! Ho ripassato tutto il piano${label}${countdown}. Chiedimi quello che vuoi.`;
  }

  if (timing.phase === 'active') {
    return `Ehi! Siamo al giorno ${timing.dayOfTrip} di ${timing.tripLength}. Chiedimi quello che vuoi sul viaggio.`;
  }

  return `Ehi! Il viaggio è finito ma conosco ancora tutto il piano${label}. Domande?`;
}

/**
 * @param {import('../trip-context.js').getContext extends () => infer R ? R : never} ctx
 * @returns {Array<{ label: string, text: string, webSearch?: boolean }>}
 */
export function buildQuickChips(ctx) {
  const timing = ctx?.trip ? getTripTiming(ctx.trip) : null;
  const chips = [];

  if (timing?.phase === 'active') {
    chips.push({ label: 'Cosa c\'è oggi?', text: 'Cosa c\'è in programma oggi?' });
    chips.push({ label: 'Domani dove andiamo?', text: 'Domani dove andiamo e cosa facciamo?' });
  } else if (timing?.phase === 'upcoming') {
    chips.push({ label: 'Primo giorno?', text: 'Com\'è il primo giorno di viaggio?' });
    chips.push({ label: 'Giornata più pesante?', text: 'Qual è la giornata più impegnativa del viaggio?' });
  } else {
    chips.push({ label: 'Riassunto viaggio', text: 'Mi fai un riassunto del viaggio giorno per giorno?' });
  }

  chips.push({
    label: 'Consigli zona',
    text: 'Hai consigli pratici sulla zona che visiteremo?',
    webSearch: true,
  });

  return chips;
}
